import { useCallback, useEffect, useState } from 'react';
import { Button, makeStyles } from '@fluentui/react-components';
import { LockClosed24Regular } from '@fluentui/react-icons';
import { useAuthStore } from '../stores/authStore.js';
import { CLAVE_VOLVER_A, esAvisoDeSesionQueSeCerro } from '../lib/sesionCerrada.js';

/**
 * Aviso de "tu sesión se cerró".
 *
 * POR QUÉ EXISTE. Cuando la sesión vencía en medio del trabajo, el usuario no recibía
 * un aviso: recibía el texto crudo del motor —"Sesión requerida"— renderizado donde
 * cayera. Podía ser un renglón rojo dentro de una tabla, un cartel al pie de un panel,
 * o nada visible si la llamada que falló no mostraba errores. Desde ahí la pantalla
 * quedaba a medias, respondiendo mal a todo, sin decir qué pasó ni qué hacer.
 *
 * QUÉ HACE. Se pone encima de todo, dice en una frase qué pasó, y ofrece el único
 * botón que sirve. Al volver a entrar, la persona aterriza en la MISMA pantalla en la
 * que estaba: la dirección se guarda antes de mandarla al inicio de sesión.
 *
 * CUÁNDO APARECE, Y CUÁNDO NO. Solo si la sesión estaba abierta. Un 401 al arrancar
 * la aplicación —el caso normal de quien todavía no entró— no es una sesión que se
 * cerró: ese lo atiende `AuthBootstrap` mandando al inicio de sesión, en silencio.
 * Por eso este aviso mira el estado de la sesión antes de mostrarse.
 *
 * Se enciende por el evento global `opus:sesion-cerrada`, que emite `apiFetch` al
 * recibir un 401 del portero (`UNAUTHENTICATED` / `INVALID_TOKEN`). Basta con que UNA
 * llamada cualquiera se lo tope: no hay que tocar los llamadores uno por uno.
 */

const useStyles = makeStyles({
  telon: {
    position: 'fixed',
    inset: 0,
    zIndex: 8_900, // debajo de "estamos actualizando": si el motor no está, manda eso
    backgroundColor: 'var(--bg-page)',
    display: 'grid',
    placeItems: 'center',
    padding: 'var(--space-xxl)',
    boxSizing: 'border-box',
  },
  caja: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-l)',
    maxWidth: '420px',
    textAlign: 'center',
  },
  icono: { color: 'var(--text-tertiary)' },
  titulo: {
    margin: 0,
    fontSize: 'var(--fs-600)',
    lineHeight: 'var(--lh-600)',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  cuerpo: {
    margin: 0,
    fontSize: 'var(--fs-400)',
    lineHeight: 'var(--lh-400)',
    color: 'var(--text-secondary)',
  },
});

export function SesionCerradaGate() {
  const styles = useStyles();
  const [visible, setVisible] = useState(false);
  const autenticado = useAuthStore((s) => s.status === 'authenticated');

  useEffect(() => {
    // Se decide ACÁ, cuando llega el 401, y no al dibujar. Ver
    // `esAvisoDeSesionQueSeCerro`: mirarlo al dibujar hacía que el 401 normal de "todavía
    // no entró" quedara prendido y saltara justo DESPUÉS de un ingreso exitoso.
    const alVencer = () => {
      if (!esAvisoDeSesionQueSeCerro(useAuthStore.getState().status)) return;
      setVisible(true);
    };
    window.addEventListener('opus:sesion-cerrada', alVencer);
    return () => window.removeEventListener('opus:sesion-cerrada', alVencer);
  }, []);

  // Si se abrió una sesión, cualquier aviso viejo dejó de valer. Es cinturón además de
  // tirantes: con la decisión tomada al llegar el 401 no debería quedar ninguno prendido.
  useEffect(() => {
    if (autenticado) setVisible(false);
  }, [autenticado]);

  const volverAEntrar = useCallback(() => {
    try {
      // La dirección de ahora, para devolverlo acá después de entrar. `sessionStorage`
      // y no `localStorage`: es de esta pestaña y de este rato, no una preferencia.
      sessionStorage.setItem(CLAVE_VOLVER_A, window.location.pathname + window.location.search);
    } catch {
      // Sin almacenamiento se pierde el regreso, no el ingreso: igual hay que entrar.
    }
    // Recarga completa y no navegación interna: la aplicación quedó con datos de una
    // sesión que ya no vale, cargados en decenas de pantallas y almacenes.
    window.location.assign('/login');
  }, []);

  if (!visible) return null;

  return (
    <div
      className={styles.telon}
      role="alertdialog"
      aria-live="assertive"
      aria-label="Tu sesión se cerró"
    >
      <div className={styles.caja}>
        <LockClosed24Regular className={styles.icono} />
        <h1 className={styles.titulo}>Tu sesión se cerró</h1>
        <p className={styles.cuerpo}>
          Por seguridad cerramos la sesión cada cierto tiempo. Tu trabajo quedó guardado:
          al volver a entrar sigues donde estabas.
        </p>
        <Button appearance="primary" size="large" onClick={volverAEntrar}>
          Volver a entrar
        </Button>
      </div>
    </div>
  );
}
